import { createApi } from '@reduxjs/toolkit/query/react';
import {
  RequestListSearchRequest,
  RequestListSearchResponse,
} from '@Types/api/requestList';
import { ApiBaseName, ApiReducerPath } from '@Enum/index';
import { customBaseQuery } from '@Utils/api/customBaseQuery';
import { PAGE_SIZE, TABLE_PAGE_DEFAULT } from '@Features/constants';
import TenderApproveRequest from '@Types/api/form/tenderRequest/TenderApproveRequest';
import { TenderVendorListResponse } from '@Types/api/form/tenderRequest/TenderVendorListResponse';
import { TenderMaterialListResponse } from '@Types/api/form/tenderRequest/TenderMaterialResponse';
import { TenderGetResponse } from '@Types/api/form/tenderRequest/TenderGetResponse';
import { TenderGetDocumentResponse } from '@Types/api/form/tenderRequest/TenderGetDocumentResponse';
import { TenderPostDocumentRequest } from '@Types/api/form/tenderRequest/TenderPostDocumentRequest';
import { TenderPostDocumentResponse } from '@Types/api/form/tenderRequest/TenderPostDocumentResponse';
import { TenderSubmitRequest } from '@Types/api/form/tenderRequest/TenderSubmitRequest';
import { TenderUpdateExtensionsRequest } from '@Types/api/form/tenderRequest/TenderUpdateExtensionsRequest';
import { TenderGetReplyItemsResponse } from '@Types/api/form/tenderRequest/TenderGetReplyItemsResponse';
import { TenderGetRepliesResponse } from '@Types/api/form/tenderRequest/TenderGetRepliesResponse';
import { VendorContactsDTO } from '@Types/api/form/tenderRequest/VendorContactsDTO';
import {
  TenderApproversResponse,
  TenderHistoryResponse,
} from '@Types/api/form';
import { TenderUpdateRequest } from '@Types/api/form/tenderRequest/TenderUpdateRequest';
import { TenderSaveQuantityRequest } from '@Types/api/form/tenderRequest/TenderSaveQuantityRquest';
import { TenderUnlockPricingRequest } from '@Types/api/form/tenderRequest/TenderUnlockPricingRequest';
import { TenderRemarkResponse } from '@Types/api/form/tenderRequest/TenderRemarkResponse';
import { TenderAddRemarkRequest } from '@Types/api/form/tenderRequest/TenderAddRemarkRequest';
import { TenderVendorAmountRequest } from '@Types/api/form/tenderRequest/TenderVendorAmountRequest';
import { TenderTotalAmountResponse } from '@Types/api/form/tenderRequest/TenderTotalAmountResponse';
import { TenderVendorAmountResponse } from '@Types/api/form/tenderRequest/TenderVendorAmountResponse';
import { TenderPriceResponse } from '@Types/api/form/tenderRequest/TenderPriceResponse';
import { TenderPriceRequest } from '@Types/api/form/tenderRequest/TenderPriceRequest';
import { TenderPricePostRequest } from '@Types/api/form/tenderRequest/TenderPricePostRequest';
import { TenderRecommendationResponse } from '@Types/api/form/tenderRequest/TenderRecommendationResponse';
import { TenderSearchVendorsResponse } from '@Types/api/form/tenderRequest/TenderSearchVendorsResponse';
import { TenderDTO } from '@Types/api/form/tenderRequest/TenderDTO';

export const tenderRequestApi = createApi({
  tagTypes: [
    'tenderRequest',
    'tenderReplies',
    'tenderTotalAmount',
    'tenderAmount',
    'tenderRecommendations',
  ],
  reducerPath: ApiReducerPath.API_TENDER_REQUEST,
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    submitTender: builder.mutation<string, TenderSubmitRequest>({
      query: ({
        requestId,
        tenderSentDate,
        tenderExpiryDate,
        prFor,
        tenderComments,
        tenderMode,
        vendorIds,
      }) => ({
        url: `${ApiBaseName.REQUISITION}/tender/${requestId}/submit`,
        method: 'post',
        params: { requestId },
        body: {
          tenderSentDate,
          tenderExpiryDate,
          prFor,
          tenderComments,
          tenderMode,
          vendorIds,
          isReinitiation: true,
        },
      }),
    }),
    searchTender: builder.mutation<
      RequestListSearchResponse<TenderDTO>,
      RequestListSearchRequest
    >({
      query: ({
        page = TABLE_PAGE_DEFAULT,
        size = PAGE_SIZE,
        sortBy = 'id',
        sortOrder = 'desc',
        searchValue,
        filters = [],
      }) => ({
        url: `${ApiBaseName.REQUISITION}/tender/search`,
        method: 'post',
        body: {
          searchCriteriaList: [
            {
              searchCriterias: filters,
              dataOperator: 'and',
            },
          ],
          dataOperator: 'and',
          searchValue,
        },
        params: { page, size, 'sort-by': sortBy, 'sort-order': sortOrder },
      }),
    }),
    cancelTender: builder.mutation<string, number>({
      query: (requestId) => ({
        url: `${ApiBaseName.REQUISITION}/tender/cancel`,
        method: 'post',
        params: { requestId },
      }),
    }),
    removeTender: builder.mutation<string, number>({
      query: (tenderId) => ({
        url: `${ApiBaseName.REQUISITION}/tender/${tenderId}/remove`,
        method: 'delete',
        params: { tenderId },
      }),
    }),
    approveTender: builder.mutation<string, TenderApproveRequest>({
      query: (body) => ({
        url: `${ApiBaseName.REQUISITION}/tender/approve`,
        method: 'post',
        body,
      }),
    }),
    getTender: builder.query<TenderGetResponse, string>({
      query: (tenderId) => ({
        url: `${ApiBaseName.REQUISITION}/tender/${tenderId}`,
        method: 'get',
        params: { tenderId },
      }),
    }),
    getVendorList: builder.query<TenderVendorListResponse, string>({
      query: (tenderId) => ({
        url: `${ApiBaseName.REQUISITION}/tender/${tenderId}/vendor`,
        method: 'get',
        params: { tenderId },
      }),
    }),
    getTenderMaterials: builder.query<TenderMaterialListResponse, string>({
      query: (tenderId) => ({
        url: `${ApiBaseName.REQUISITION}/tender/${tenderId}/material`,
        method: 'get',
        params: { tenderId },
      }),
    }),
    getTenderDocuments: builder.query<TenderGetDocumentResponse[], string>({
      query: (tenderId) => ({
        url: `${ApiBaseName.REQUISITION}/tender/${tenderId}/purchase/document`,
        method: 'get',
        params: { tenderId },
      }),
    }),
    postTenderDocument: builder.mutation<
      TenderPostDocumentResponse,
      TenderPostDocumentRequest
    >({
      query: ({ body, tenderId }) => ({
        url: `${ApiBaseName.REQUISITION}/tender/${tenderId}/document`,
        method: 'post',
        body,
      }),
    }),
    deleteTenderDocument: builder.mutation<null, string | number>({
      query: (tenderId) => ({
        url: `${ApiBaseName.REQUISITION}/tender/${tenderId}/document`,
        method: 'delete',
      }),
    }),
    deleteTenderMaterials: builder.mutation<
      null,
      { tenderId: string | number; tenderItemIds: string[] }
    >({
      query: ({ tenderId, tenderItemIds }) => ({
        url: `${ApiBaseName.REQUISITION}/tender/${tenderId}/material`,
        method: 'delete',
        params: { tenderItemIds },
      }),
    }),
    updateTenderMode: builder.mutation<null, string | number>({
      query: (tenderId) => ({
        url: `${ApiBaseName.REQUISITION}/tender/update_mode`,
        method: 'post',
        params: {
          requestId: tenderId,
        },
      }),
    }),
    updateTenderExtensions: builder.mutation<
      null,
      TenderUpdateExtensionsRequest
    >({
      query: (body) => ({
        url: `${ApiBaseName.REQUISITION}/tender/update_extensions`,
        method: 'post',
        body,
      }),
    }),
    getTenderReplies: builder.query<TenderGetRepliesResponse, string>({
      query: (tenderId) => ({
        url: `${ApiBaseName.REQUISITION}/reply/list/${tenderId}`,
        method: 'get',
      }),
      providesTags: ['tenderReplies'],
    }),
    getTenderReplyItems: builder.mutation<TenderGetReplyItemsResponse, string>({
      query: (replyId) => ({
        url: `${ApiBaseName.REQUISITION}/reply/items/${replyId}`,
        method: 'get',
      }),
    }),
    getTenderVendorContacts: builder.mutation<VendorContactsDTO[], string>({
      query: (vendorId) => ({
        url: `${ApiBaseName.REQUISITION}/vendor/${vendorId}/contacts`,
        method: 'get',
      }),
    }),
    getTenderHistory: builder.query<TenderHistoryResponse[], number>({
      query: (tenderId) => ({
        url: `${ApiBaseName.REQUISITION}/tender/${tenderId}/history`,
        method: 'get',
      }),
    }),
    getTenderApprovers: builder.query<TenderApproversResponse, number>({
      query: (tenderId) => ({
        url: `${ApiBaseName.REQUISITION}/tender/${tenderId}/approvers`,
        method: 'get',
      }),
    }),
    updateTender: builder.mutation<
      null,
      { tenderId: string; body: TenderUpdateRequest }
    >({
      query: ({ tenderId, body }) => ({
        url: `${ApiBaseName.REQUISITION}/tender/${tenderId}/update`,
        method: 'post',
        body,
      }),
      invalidatesTags: ['tenderReplies', 'tenderTotalAmount', 'tenderAmount'],
    }),
    saveTenderQuantity: builder.mutation<null, TenderSaveQuantityRequest>({
      query: (body) => ({
        url: `${ApiBaseName.REQUISITION}/tender/save-quantity`,
        method: 'post',
        body,
      }),
      invalidatesTags: ['tenderTotalAmount', 'tenderReplies'],
    }),
    unlockTenderPricing: builder.mutation<null, TenderUnlockPricingRequest>({
      query: (body) => ({
        url: `${ApiBaseName.REQUISITION}/tender/unlock`,
        method: 'post',
        body,
      }),
    }),
    getTenderRemarks: builder.query<TenderRemarkResponse[], string>({
      query: (tenderId) => ({
        url: `${ApiBaseName.REQUISITION}/tender/${tenderId}/remarks`,
        method: 'get',
      }),
    }),
    postTenderRemark: builder.mutation<null, TenderAddRemarkRequest>({
      query: (body) => ({
        url: `${ApiBaseName.REQUISITION}/tender/recommendation`,
        method: 'post',
        body,
      }),
      invalidatesTags: ['tenderRecommendations'],
    }),
    getTenderTotalAmount: builder.query<
      TenderTotalAmountResponse,
      number | string
    >({
      query: (tenderId) => ({
        url: `${ApiBaseName.REQUISITION}/tender/tot-amount`,
        method: 'get',
        params: { tenderId },
      }),
      providesTags: ['tenderTotalAmount'],
    }),
    getTenderVendorAmount: builder.query<
      TenderVendorAmountResponse,
      TenderVendorAmountRequest
    >({
      query: ({ tenderId, vendorId }) => ({
        url: `${ApiBaseName.REQUISITION}/tender/amount`,
        method: 'get',
        params: { tenderId, vendorId },
      }),
      providesTags: ['tenderAmount'],
    }),

    getTenderPrice: builder.query<TenderPriceResponse[], TenderPriceRequest>({
      query: ({ tenderId }) => ({
        url: `${ApiBaseName.REQUISITION}/tender/price`,
        method: 'get',
        params: { requestId: tenderId },
      }),
    }),
    postTenderPrice: builder.mutation<null, TenderPricePostRequest[]>({
      query: (body) => ({
        url: `${ApiBaseName.REQUISITION}/tender/price`,
        method: 'post',
        body,
      }),
    }),
    getTenderAccess: builder.query<number, string>({
      query: (tenderId) => ({
        url: `${ApiBaseName.REQUISITION}/tender/${tenderId}/access`,
        method: 'get',
      }),
    }),
    getTenderRecommendation: builder.query<
      TenderRecommendationResponse,
      { tenderId: number; vendorId: number }
    >({
      query: ({ tenderId, vendorId }) => ({
        url: `${ApiBaseName.REQUISITION}/tender/recommendation`,
        method: 'get',
        params: { tenderId, vendorId },
      }),
      providesTags: ['tenderRecommendations'],
    }),
    searchTenderVendors: builder.query<
      TenderSearchVendorsResponse[],
      { tenderId: number; tenderMode: 1 | 2 }
    >({
      query: ({ tenderId, tenderMode }) => ({
        url: `${ApiBaseName.REQUISITION}/vendor/vendors`,
        method: 'get',
        params: { tenderId, tenderMode },
      }),
    }),
    checkHasTenderTask: builder.mutation<string, number>({
      query: (tenderId) => ({
        url: `${ApiBaseName.REQUISITION}/tender/checkTask`,
        method: 'get',
        params: { tenderId },
      }),
    }),
  }),
});

export const {
  useCheckHasTenderTaskMutation,
  useGetTenderPriceQuery,
  usePostTenderPriceMutation,
  useGetTenderTotalAmountQuery,
  useGetTenderVendorAmountQuery,
  useGetTenderRemarksQuery,
  usePostTenderRemarkMutation,
  useGetTenderApproversQuery,
  useLazyGetTenderHistoryQuery,
  useSubmitTenderMutation,
  useSearchTenderMutation,
  useCancelTenderMutation,
  useRemoveTenderMutation,
  useApproveTenderMutation,
  useGetTenderQuery,
  useGetVendorListQuery,
  useGetTenderMaterialsQuery,
  useGetTenderDocumentsQuery,
  usePostTenderDocumentMutation,
  useDeleteTenderDocumentMutation,
  useDeleteTenderMaterialsMutation,
  useUpdateTenderModeMutation,
  useUpdateTenderExtensionsMutation,
  useGetTenderRepliesQuery,
  useGetTenderReplyItemsMutation,
  useGetTenderVendorContactsMutation,
  useUpdateTenderMutation,
  useSaveTenderQuantityMutation,
  useUnlockTenderPricingMutation,
  useGetTenderAccessQuery,
  useLazyGetTenderAccessQuery,
  useGetTenderRecommendationQuery,
  useSearchTenderVendorsQuery,
} = tenderRequestApi;
